import type { ReactNode } from 'react';
import { Button, Flex, Tooltip, theme } from 'antd';
import { Logotipo } from './Logotipo';

/**
 * O topo da barra lateral: a marca e o botão que recolhe.
 *
 * Vem da Voa, e são duas ideias:
 *
 * - **O logotipo vira símbolo ao recolher.** A alternativa - sumir com a marca,
 *   que era o que fazíamos - deixava a coluna estreita começando com um botão
 *   solto, sem nada dizendo de que produto é aquela fila de ícones;
 * - **O botão é discreto.** Recolher a barra é gesto de quem já mora na tela;
 *   não disputa atenção com a navegação. Daí o ícone de 16px num botão pequeno,
 *   e não o `MenuFold` de 32px que ele substitui.
 *
 * Recolhida, a marca e o botão empilham e centralizam - lado a lado eles não
 * cabem, e o símbolo sozinho ficaria torto.
 *
 * Vive no pacote de tema porque app e admin têm a mesma barra: duplicá-la faria
 * as duas divergirem no primeiro ajuste.
 */

/**
 * O ícone do recolhimento: o painel com a coluna à esquerda.
 *
 * É o mesmo desenho nos dois estados, e de propósito - ele nomeia o objeto
 * (a barra lateral), não a direção do movimento. Seta que aponta para um lado e
 * depois para o outro obriga a decifrar para onde a coisa vai; o painel só diz
 * "isto aqui é a barra", e o rótulo acessível conta o resto.
 */
function IconeDaBarra() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      aria-hidden
      focusable="false"
    >
      <rect x="1.5" y="2.5" width="13" height="11" rx="2" />
      <line x1="6" y1="2.5" x2="6" y2="13.5" />
    </svg>
  );
}

export function CabecalhoDaBarra({
  recolhida,
  aoAlternar,
  sufixo,
  altura = 64,
}: {
  recolhida: boolean;
  aoAlternar: (recolhida: boolean) => void;
  /**
   * O que vem depois do logotipo quando expandido - o "Admin" do backoffice.
   *
   * **Ele custa altura de marca**, e por isso o logotipo mede menos quando há
   * sufixo. A barra tem 224px no backoffice; tirando os 32 de recuo, o vão de
   * 8, o botão de recolher de 24 e o vão de 8 do grupo da esquerda, sobram 152
   * para logotipo e sufixo - e o "Admin" leva 40,8 deles, medido em IBM Plex
   * Sans de 14. Restam **111,2**, e o logotipo é 6,35 vezes mais largo que alto:
   * dá 17 de altura, com 3,3px de folga.
   *
   * No app do aluno não há sufixo e a barra tem 232, o que deixa 168 para os
   * 152,3 de um logotipo de 24.
   *
   * **Eram 18, e caíram para 17 quando o lápis virou livro.** O símbolo passou
   * de 0,73 para 1,22 de largura sobre altura, e o logotipo inteiro de 5,74
   * para 6,35: no mesmo 18 ele mediria 114,2 e passaria 3px do que a barra tem
   * para dar - e como o `Logotipo` não encolhe (`flexShrink: 0`) e o "Admin"
   * não quebra linha (`whiteSpace: nowrap`), o que cede é o botão. Quem mudar o
   * símbolo de novo refaz esta conta; a razão da largura está no README da
   * pasta `assets/brand`.
   */
  sufixo?: ReactNode;
  /** Altura da faixa. Casa com o cabeçalho do conteúdo quando há um. */
  altura?: number;
}) {
  const { token } = theme.useToken();
  const rotulo = recolhida ? 'Expandir menu' : 'Recolher menu';

  const botao = (
    <Tooltip title={rotulo} placement="right">
      <Button
        type="text"
        size="small"
        aria-label={rotulo}
        aria-expanded={!recolhida}
        icon={<IconeDaBarra />}
        onClick={() => aoAlternar(!recolhida)}
        style={{ color: token.colorTextTertiary }}
      />
    </Tooltip>
  );

  if (recolhida) {
    return (
      <Flex
        vertical
        align="center"
        justify="center"
        gap={token.marginXXS}
        style={{ paddingBlock: token.paddingSM, borderBottom: `1px solid ${token.colorSplit}` }}
      >
        <Logotipo apenasSimbolo altura={26} />
        {botao}
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={token.marginXS}
      style={{
        height: altura,
        paddingInline: token.padding,
        borderBottom: `1px solid ${token.colorSplit}`,
      }}
    >
      <Flex align="center" gap={token.marginXS} style={{ minWidth: 0 }}>
        <Logotipo altura={sufixo ? 17 : 24} />
        {sufixo}
      </Flex>
      {botao}
    </Flex>
  );
}
